import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Central route-level scroll manager (HandyHub AU).
 *
 * Why this exists: react-router v7's <BrowserRouter> ships no scroll
 * restoration, and the browser's default history.scrollRestoration='auto'
 * fights any scrollTo(0,0) we do — which left many pages "opening at the
 * bottom" (retaining the previous route's offset). We take manual control:
 *
 *  - PUSH / REPLACE to a new pathname  → scroll to top (after paint).
 *  - URL with a #hash                  → scroll to that element, offset for
 *                                        the sticky header, and focus it.
 *  - Browser Back / Forward (POP)      → restore the saved scroll position
 *                                        (retrying while async content grows).
 *  - Same pathname, only ?search change → do NOT scroll (preserves filters).
 *
 * Route jumps use instant ('auto') scrolling, which is the correct,
 * reduced-motion-friendly default.
 */

const positions = new Map(); // location.key -> scrollY

const headerOffset = () => {
  const h = document.querySelector(".header");
  return (h ? h.offsetHeight : 72) + 16;
};

export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  const prevPathname = useRef(location.pathname);
  const lastY = useRef(0);

  // Take manual control of scroll restoration for the app's lifetime.
  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return;
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => { window.history.scrollRestoration = prev; };
  }, []);

  // Per-location effect: track this page's scroll and, on cleanup (i.e. when
  // navigating AWAY), persist its final position under the CORRECT key. The
  // closure captures `key`, so the destination page resetting to 0 can never
  // clobber the page we're leaving — this was the bug in the naive version.
  useEffect(() => {
    const key = location.key;
    lastY.current = window.scrollY;
    const onScroll = () => { lastY.current = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      positions.set(key, lastY.current);
      window.removeEventListener("scroll", onScroll);
    };
  }, [location.key]);

  // Decision effect: where should the new page land?
  useEffect(() => {
    const outgoingPathname = prevPathname.current;
    prevPathname.current = location.pathname;

    const scrollTop = () => window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    const scrollToHash = () => {
      const id = decodeURIComponent(location.hash.slice(1));
      if (!id) return false;
      const el = document.getElementById(id);
      if (!el) return false;
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset();
      window.scrollTo({ top: Math.max(top, 0), left: 0, behavior: "auto" });
      if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
      try { el.focus({ preventScroll: true }); } catch { /* ignore */ }
      return true;
    };

    // Restore a saved position, retrying briefly while async content grows tall
    // enough to reach the target. Prioritises predictable behaviour: gives up
    // after ~1.2s and lands as close as possible.
    let restoreTimer = 0;
    const restoreTo = (target) => {
      const started = Date.now();
      const tick = () => {
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo({ top: Math.min(target, Math.max(maxScroll, 0)), left: 0, behavior: "auto" });
        if (maxScroll < target && Date.now() - started < 1200) {
          restoreTimer = window.setTimeout(tick, 60);
        }
      };
      tick();
    };

    let raf1 = 0, raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (navType === "POP") {
          const saved = positions.get(location.key);
          if (typeof saved === "number" && saved > 0) { restoreTo(saved); return; }
          if (location.hash && scrollToHash()) return;
          scrollTop();
          return;
        }
        // PUSH / REPLACE
        if (location.hash) { if (scrollToHash()) return; }
        // Only reset to top when the pathname actually changed — leave in-page
        // ?search / filter updates (same path) exactly where the user is.
        if (location.pathname !== outgoingPathname) scrollTop();
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); if (restoreTimer) clearTimeout(restoreTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.pathname, location.hash, navType]);

  return null;
}
